import { RankedListWidget } from '../RankedListWidget/ranked-list-widget';
import { useTranslation } from 'react-i18next';
import { toNumberString } from '@client/toolkit';
import { usePostOccupancyRatesDashboardMutation } from '@client/api';
import { useEffect, useMemo } from 'react';
import { NoDataWidget } from '../NoDataWidget/no-data-widget';

interface OccupancyRateWidgetProps {
  buildingIds: string[];
  selectedDateFrom: string;
  selectedDateTo: string;
}

export const OccupancyRateWidget = ({ buildingIds, selectedDateFrom, selectedDateTo }: OccupancyRateWidgetProps) => {
  const { t } = useTranslation('widget');
  const [trigger, { isLoading, data }] = usePostOccupancyRatesDashboardMutation();

  const topThreeHighest = useMemo(() => {
    return data?.topBuildings.map((item, i) => ({
      id: `top-${i}`,
      label: item.buildingName,
      value: item.occupancyRate,
    }));
  }, [data?.topBuildings]);
  const bottomThreeLowest = useMemo(() => {
    return data?.bottomBuildings.map((item, i) => ({
      id: `bottom-${i}`,
      label: item.buildingName,
      value: item.occupancyRate,
    }));
  }, [data?.bottomBuildings]);

  const noDataAvailable = useMemo(() => {
    return data?.occupancyRate === 0 && data.topBuildings.length === 0 && data.bottomBuildings.length === 0;
  }, [data?.occupancyRate, data?.topBuildings, data?.bottomBuildings]);

  useEffect(() => {
    if (!selectedDateFrom || !selectedDateTo) return;
    trigger({
      startDate: selectedDateFrom,
      endDate: selectedDateTo,
      body: {
        buildingIds: buildingIds,
      },
    });
  }, [selectedDateFrom, selectedDateTo, buildingIds]);

  if (noDataAvailable && !isLoading)
    return <NoDataWidget title={t('occupancyRate.title')} noDataText={t('occupancyRate.noData')} />;

  return (
    <RankedListWidget
      loading={isLoading}
      to="/reporting/occupancy-rate"
      title={t('occupancyRate.title')}
      tooltip={t('occupancyRate.tooltip')}
      value={t('occupancyRate.displayValue', { value: toNumberString(data?.occupancyRate) })}
      unit={t('occupancyRate.unit')}
      rankUnit={t('occupancyRate.rankUnit')}
      highListTitle={t('occupancyRate.topThreeHighest')}
      lowListTitle={t('occupancyRate.bottomThreeLowest')}
      highListData={topThreeHighest || []}
      lowListData={bottomThreeLowest || []}
    />
  );
};
